import { useState, useCallback, useEffect } from 'react';
import { X, Bug, AlertCircle, Shield } from 'lucide-react';
import { createBugReport } from '../../api/bugReports';
import { collectDeviceInfo, enrichDeviceInfoWithServiceWorker } from '../../lib/deviceInfo';
import { useAuth } from '../../hooks/useAuth';
import { useProject } from '../../hooks/useProject';
import { useRecentInteractions } from '../../hooks/useRecentInteractions';
import { Modal } from '../common/Modal';
import type { BugReportSeverity } from '../../types';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  captureConsole: () => string[];
  routePath: string;
  pageUrl: string;
}

const SEVERITY_OPTIONS: { value: BugReportSeverity; label: string }[] = [
  { value: 'low', label: 'Düşük' },
  { value: 'medium', label: 'Orta' },
  { value: 'high', label: 'Yüksek' },
  { value: 'critical', label: 'Kritik' },
];

export function BugReportModal({
  isOpen,
  onClose,
  captureConsole,
  routePath,
  pageUrl,
}: BugReportModalProps) {
  const { user } = useAuth();
  const { selectedProject } = useProject();
  const getRecentInteractions = useRecentInteractions();
  const [comment, setComment] = useState('');
  const [severity, setSeverity] = useState<BugReportSeverity>('medium');
  const [includeConsole, setIncludeConsole] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setComment('');
      setSeverity('medium');
      setIncludeConsole(true);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);
      setError(null);

      try {
        const session = {
          role: user?.role ?? null,
          roleId: user?.role_id ?? null,
          projectId: selectedProject?.id ?? null,
          projectName: selectedProject?.name ?? null,
        };
        const deviceInfo = collectDeviceInfo(routePath, pageUrl, session, getRecentInteractions());
        await createBugReport({
          page_url: pageUrl,
          route_path: routePath,
          comment: comment.trim() || null,
          console_logs: includeConsole ? captureConsole() : null,
          device_info: await enrichDeviceInfoWithServiceWorker(deviceInfo),
          severity,
        });
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (err: unknown) {
        const message =
          (err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined) ?? 'Rapor gönderilemedi. Lütfen tekrar deneyin.';
        setError(message);
      } finally {
        setSubmitting(false);
      }
    },
    [
      comment,
      severity,
      includeConsole,
      pageUrl,
      routePath,
      captureConsole,
      onClose,
      user,
      selectedProject,
      getRecentInteractions,
    ]
  );

  if (!isOpen) return null;

  return (
    <Modal
      open
      onClose={onClose}
      align="sheet"
      zIndex={110}
      overlayClassName="bg-black/50 p-0 sm:p-4"
      panelUnstyled
      panelClassName="flex max-h-[90vh] w-full flex-col rounded-t-xl bg-white shadow-2xl sm:max-w-lg sm:rounded-xl"
      labelledBy="bug-report-modal-title"
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2 text-slate-800">
          <Bug className="h-5 w-5 text-amber-600" />
          <h2 id="bug-report-modal-title" className="text-lg font-semibold">
            Hata Bildir
          </h2>
        </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Kapat"
            data-testid="bug-report-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {success ? (
            <div className="rounded-lg bg-green-50 p-4 text-center text-green-700">
              <p className="font-medium">Hata raporunuz gönderildi.</p>
              <p className="mt-1 text-sm">Katkınız için teşekkürler.</p>
              <p className="mt-2 text-xs">
                Raporunuzun durumunu <strong>Destek</strong> bölümünden takip edebilirsiniz.
              </p>
            </div>
          ) : (
            <form id="bug-report-form" onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Sayfa</label>
                <p className="truncate rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                  {routePath || pageUrl}
                </p>
              </div>

              <div>
                <label htmlFor="bug-severity" className="mb-1 block text-sm font-medium text-slate-700">
                  Önem Derecesi
                </label>
                <select
                  id="bug-severity"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as BugReportSeverity)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  data-testid="bug-report-severity"
                >
                  {SEVERITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="bug-comment" className="mb-1 block text-sm font-medium text-slate-700">
                  Açıklama <span className="font-normal text-slate-500">(isteğe bağlı)</span>
                </label>
                <textarea
                  id="bug-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  maxLength={5000}
                  placeholder="Hatayı tarif edin: ne yaptınız, ne görmeyi bekliyordunuz, ne oldu?"
                  className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  data-testid="bug-report-comment"
                />
                <p className="mt-1 text-right text-xs text-slate-400">{comment.length}/5000</p>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={includeConsole}
                  onChange={(e) => setIncludeConsole(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                  data-testid="bug-report-include-console"
                />
                Son console loglarını ekle ({captureConsole().length} satır)
              </label>

              <div className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                <Shield className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <p>
                  Gönderilen veriler: cihaz/tarayıcı bilgisi, ekran boyutu, ağ durumu, mevcut sayfa,
                  oturum (rol, proje) ve son kullanıcı etkileşimleri. Console loglarındaki token,
                  e-posta ve şifre benzeri hassas içerikler otomatik maskelenir.
                </p>
              </div>
            </form>
          )}
        </div>

        {!success && (
          <div className="flex items-center justify-end gap-2 border-t border-slate-100 p-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
              data-testid="bug-report-cancel"
            >
              İptal
            </button>
            <button
              type="submit"
              form="bug-report-form"
              disabled={submitting}
              className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60"
              data-testid="bug-report-submit"
            >
              {submitting ? 'Gönderiliyor...' : 'Gönder'}
            </button>
        </div>
      )}
    </Modal>
  );
}
